import React, { useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeOrder = (order = {}) => ({
  ...order,
  id: Number(getSalesId(order) || 0),
  jobNumber: String(getSalesValue(order, 'jobNumber', 'JobNumber') || ''),
  projectName: String(getSalesValue(order, 'projectName', 'ProjectName') || ''),
  contactPerson: String(getSalesValue(order, 'contactPerson', 'ContactPerson') || ''),
  orderDate: getSalesValue(order, 'orderDate', 'OrderDate'),
  requestedDeliveryDate: getSalesValue(order, 'requestedDeliveryDate', 'RequestedDeliveryDate'),
  isSchedule: Boolean(getSalesValue(order, 'isSchedule', 'IsSchedule') ?? false),
  pbInvoicePrice: Number(getSalesValue(order, 'pbInvoicePrice', 'PBInvoicePrice') || 0),
});

const normalizeOrders = (rows) => rows.map(normalizeOrder);

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const OrderSheetList = () => {
  const { isAdmin } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');

  const { items: orders, loading, error } = useSalesApiList((endpoints) => endpoints.salesQuoteOrders.base, {
    normalize: normalizeOrders,
    errorMessage: 'Unable to load order sheets.',
  });

  const filteredOrders = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((order) =>
      [order.projectName, order.jobNumber, order.contactPerson, formatDate(order.orderDate), order.pbInvoicePrice]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [orders, searchTerm]);

  return (
    <section className="sales-portal-page" aria-labelledby="sales-order-sheets-title">
      <Row className="mb-3">
        <Col><h4 id="sales-order-sheets-title">Order Sheet List</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>Job Number</th>
                  <th>Project Name</th>
                  <th>Contact Person</th>
                  <th>Order Date</th>
                  <th>Delivery Date</th>
                  <th>Invoice Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading order sheets...</div></td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7}><div className="sales-no-data">No order sheets found.</div></td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.jobNumber}</td>
                      <td>{order.projectName}</td>
                      <td>{order.contactPerson}</td>
                      <td>{formatDate(order.orderDate)}</td>
                      <td>{order.isSchedule ? 'Per Schedule' : formatDate(order.requestedDeliveryDate)}</td>
                      <td>{formatCurrency(order.pbInvoicePrice)}</td>
                      <td>
                        <Link to={`/sales/order-sheet/detail/${order.id}`} className="edit-icon" title="Edit">
                          <i className="bi bi-pencil-square"></i>
                        </Link>
                        {isAdmin() && (
                          <button type="button" className="edit-icon" title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderSheetList;
